import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  Output,
  SimpleChanges
} from '@angular/core';

import {
  CustomerThread,
  MessageLog
}                                  from './typings/customer-messages.customer-thread-params';
import { CustomerMessagesService } from './customer-messages.service';

type ListTab    = 'all' | 'unread';
type MenuAction = 'view_customer' | 'open_latest_invoice' | 'mark_unread';

@Component({
  selector   : 'wor-customer-messages-customer-list',
  styles: [require('./customer-messages.customer-list.component.scss')],
  template: require('./customer-messages.customer-list.component.html')
})
export class CustomerMessagesCustomerListComponent implements OnChanges {
  @Input() threads: CustomerThread[] = [];

  @Input() canLoadMore        = false;
  @Input() loadingMore        = false;
  @Output() loadMore          = new EventEmitter<void>();
  @Output() menuAction        = new EventEmitter<{ action: MenuAction; thread: CustomerThread }>();
  @Output() newMessage        = new EventEmitter<void>();
  @Output() selectThread      = new EventEmitter<CustomerThread>();
  @Output() selectNewCustomer = new EventEmitter<any>();

  filtered      : CustomerThread[]= [];
  newMessageMode                  = false;
  newCustomer   : any             = null;
  openMenuFor   : string | null   = null;
  query                           = '';
  searchCustomer: any             = null;
  searchOpen                      = false;
  searchQuery                     = '';
  tab           : ListTab         = 'all';

  constructor(
    private customerMessagesService : CustomerMessagesService,
    private elRef                   : ElementRef<HTMLElement>
  ) {}

  applyFilters () : void {
    const threads = Array.isArray(this.threads) ? this.threads : [];

    const q = (this.query || '').trim().toLowerCase();

    this.filtered = threads.filter(thread => {
      if (this.tab === 'unread' && !this.isThreadUnread(thread)) {
        return false;
      }

      if (!q) {
        return true;
      }

      // Search through customer
      const name    = this.getDisplayName(thread).toLowerCase();
      const company = (thread.company_name || '').toLowerCase();
      const mobile  = (thread.mobile || '').toLowerCase();

      if (name.includes(q) || company.includes(q) || mobile.includes(q)) {
        return true;
      }

      // Search through messages
      const msgs = Array.isArray(thread.message_logs) ? thread.message_logs : [];
      return msgs.some(m => (m?.message || '').toLowerCase().includes(q));
    });
  }

  closeSearch () : void {
    this.searchOpen = false;
    this.setQuery('');
  }

  enterNewMessageMode () : void {
    this.newMessageMode = true;
    this.newCustomer = null;
  }

  exitNewMessageMode () : void {
    this.newMessageMode = false;
    this.newCustomer = null;
  }

  formatResultName( result : any ) : string {
    const first = (result?.first_name || '').trim();
    const last  = (result?.last_name || '').trim();
    const full  = `${first} ${last}`.trim();
    return full || (result?.company_name || 'Customer');
  }

  getDisplayName ( thread : CustomerThread ) : string {
    const first = (thread.first_name || '').trim();
    const last  = (thread.last_name || '').trim();
    const full  = `${first} ${last}`.trim();

    if (full) {
      return full;
    }
    if (thread.company_name) {
      return String(thread.company_name);
    }
    return `Customer ${thread.customer_id?.slice(0, 6) ?? ''}`.trim();
  }

  getLatestMessage ( thread : CustomerThread ) : MessageLog | null {
    const msgs = Array.isArray(thread.message_logs) ? thread.message_logs : [];
    return msgs.length ? msgs[0] : null;
  }

  getLatestSnippet ( thread : CustomerThread ) : string {
    const msg = this.getLatestMessage(thread);
    return (msg?.message || '').trim();
  }

  getInitials ( thread : CustomerThread ) : string {
    const name = this.getDisplayName(thread).trim();
    const parts =
      name
     .split(/\s+/)
     .filter(Boolean);

    const a   = parts[0]?.[0] ?? '';
    const b   = parts[1]?.[0] ?? '';
    const out = (a + b).toUpperCase();

    return out || (name[0]?.toUpperCase() ?? '?');
  }

  // Avatar Colors
  getAvatarThemeClass ( thread : CustomerThread ) : string {
    const key = String(thread.customer_id || this.getDisplayName(thread) || '');
    const n   = this.hashToInt(key) % 20;
    return `avatar-theme-${n}`;
  }

  getTimeLabel ( thread : CustomerThread ) : string {
    const ms = this.parseDates(this.getLatestMessage(thread)?.created_on);
    if (!ms) {
      return '';
    }

    const diff = Math.max(0, Date.now() - ms);

    const min = Math.floor(diff / 60000);
    if (min < 60) {
      const v = Math.max(1, min);
      return v === 1 ? '1 min' : `${v} mins`;
    }

    const hr = Math.floor(diff / 3600000);
    if (hr < 24) {
      return hr === 1 ? '1 hour' : `${hr} hours`;
    }

    const day = Math.floor(diff / 86400000);
    if (day < 7) {
      return day === 1 ? '1 day' : `${day} days`;
    }

    const d = new Date(ms);
    return d.toLocaleDateString(undefined, {
      month: 'short',
      day  : 'numeric'
    });
  }

  hashToInt ( string : string ) : number {
    let h = 0;
    for (let i = 0; i < string.length; i++) {
      h = ((h << 5) - h) + string.charCodeAt(i);
      h |= 0;
    }
    return Math.abs(h);
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  handleModelChange ( customer : any ) : void {}

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  handleModelClick ( event : any ) : void {}

  isThreadUnread ( thread : CustomerThread ) : boolean {
    if (typeof thread.unread === 'boolean') {
      return thread.unread;
    }
    const msgs = Array.isArray(thread.message_logs) ? thread.message_logs : [];
    return msgs.some(msg => msg?.unread === true);
  }

  ngOnChanges ( changes : SimpleChanges ) : void {
    if (changes.threads || changes.searchQuery || changes.searchOpen) {
      this.query = (this.searchOpen ? (this.searchQuery || '') : '');
      this.applyFilters();
    }
  }

  openSearch () : void {
    this.searchOpen = true;
  }

  onSearchInput ( event : Event ) : void {
    const value = (event.target as HTMLInputElement).value;
    this.setQuery(value);
  }

  onRowMenuAction ( action : MenuAction, thread : CustomerThread, event : MouseEvent ) : void {
    event.stopPropagation();
    this.openMenuFor = null;
    this.menuAction.emit({ action, thread: thread });
  }

  @HostListener('document:click', ['$event'])
  onDocClick ( event : MouseEvent ) : void {
    const target = event.target as HTMLElement | null;
    if (!target) {
      return;
    }

    // close - click outside
    if (!this.elRef.nativeElement.contains(target)) {
      this.openMenuFor = null;
    }
  }

  onNewCustomerSelected ( value : any ) : void {
    if (typeof value === 'string') {
      return;
    }

    if (!value) {
      return;
    }

    const customerId = value.customer_id || value.id;
    if (!customerId) {
      return;
    }

    this.selectNewCustomer.emit(value);
    this.exitNewMessageMode();
  }

  onSearchCustomerSelected ( value : any ) : void {
    if (typeof value === 'string') {
      return;
    }

    if (!value) {
      return;
    }

    const customerId = value.customer_id || value.id;
    if (!customerId) {
      return;
    }

    const existing = (this.threads || []).find(t => t.customer_id === customerId);

    if (existing) {
      this.selectThread.emit(existing);
    } else {
      this.selectNewCustomer.emit(value);
    }

    this.searchCustomer = null;
  }

  onSelect ( thread : CustomerThread ) : void {
    this.selectThread.emit(thread);
  }

  onLoadMore () : void {
    if (this.loadingMore) {
      return;
    }
    this.loadMore.emit();
  }

  onNewMessage () : void {
    this.newMessage.emit();
  }

  parseDates ( dateTime : string | null | undefined ) : number {
    if (!dateTime) {
      return 0;
    }
    const dt = Date.parse(dateTime);
    return Number.isFinite(dt) ? dt : 0;
  }

  search = ( typed: string ) => {
    const q = (typed || '').trim();
    if (!q) {
      return Promise.resolve([]);
    }

    return Promise.resolve(this.customerMessagesService.search(q) as any);
  };

  setTab ( tab : ListTab ) : void {
    this.tab = tab;
    this.applyFilters();
  }

  setQuery ( q : string ) : void {
    this.query = q;
    this.applyFilters();
  }

  toggleRowMenu ( thread : CustomerThread, event : MouseEvent ) : void {
    event.stopPropagation();
    this.openMenuFor =
      (this.openMenuFor === thread.customer_id)
      ? null
      : thread.customer_id;
  }

}
